<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6" v-if="order">
    <!-- Заголовок заказа -->
    <div class="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 mb-8">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center flex-wrap gap-3">
            <!-- Номер заказа — его собственный number, при пустом id. Тот же вид на столе СМК,
                 в реестре несоответствий, в селекторе привязки и в протоколе ПСИ -->
            <h1 class="text-2xl font-bold text-slate-800">
              Заказ № {{ order.number || order.id }}
            </h1>
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  :class="getStatusBadgeClass(order.status)">
              {{ getStatusLabel(order.status) }}
            </span>
          </div>
          <div class="mt-1 text-sm text-slate-500">
            Дата заказа: {{ formatDate(order.order?.created_at || order.created_at) }}
          </div>
        </div>
        <button
          @click="goBackToTasks"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          К списку заданий
        </button>
      </div>

      <!-- Ключевые параметры заказа одинаковыми плитками, чтобы шапка не «ступенькой» -->
      <div class="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div class="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
          <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </span>
          <div class="min-w-0">
            <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Продукция</div>
            <div class="text-base font-semibold text-slate-800 break-words">
              <span :class="{ 'line-through text-gray-500': (order.order_product?.product || order.product)?.deleted_at }">
                {{ order.order_product?.product?.name || order.product?.name || 'Не указано' }}
              </span>
              <span v-if="(order.order_product?.product || order.product)?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
            </div>
          </div>
        </div>

        <div class="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
          <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </span>
          <div class="min-w-0">
            <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Спецификация</div>
            <div class="text-base font-semibold text-slate-800 break-words">
              <span :class="{ 'line-through text-gray-500': order.tech_map?.deleted_at }">
                {{ order.tech_map?.name || 'Не указано' }}
              </span>
              <span v-if="order.tech_map?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
            </div>
          </div>
        </div>

        <div class="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
          <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </span>
          <div class="min-w-0">
            <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Количество</div>
            <div class="text-base font-semibold text-slate-800">{{ formatQuantity(order.count) }} {{ getUnitLabel(order.order_product?.product || order.product) }}</div>
          </div>
        </div>

        <div class="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
          <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <div class="min-w-0">
            <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Изделий готово</div>
            <!-- Прогресс считает сервер: правило про забракованные задания одно на все экраны -->
            <div class="text-base font-semibold text-slate-800">{{ order.completed_task_count ?? 0 }} из {{ order.task_count ?? 0 }} шт.</div>
          </div>
        </div>
      </div>

      <!-- Плановые и фактические даты заказа отдельным блоком -->
      <div class="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-0 sm:divide-x sm:divide-slate-200">
          <div v-for="(group, groupIndex) in headerDateGroups" :key="group.key"
               :class="['flex items-start gap-3', groupIndex === 0 ? 'sm:pr-6' : 'sm:pl-6']">
            <span class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" :class="group.iconClass">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="group.iconPath" />
              </svg>
            </span>
            <div class="min-w-0 flex-1">
              <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{{ group.title }}</div>
              <dl class="mt-1 space-y-0.5">
                <div v-for="row in group.rows" :key="row.key" class="flex items-baseline justify-between gap-3">
                  <dt class="text-xs text-slate-500">{{ row.label }}</dt>
                  <dd class="text-sm font-medium tabular-nums" :class="row.value ? 'text-slate-700' : 'text-slate-400'">
                    {{ row.value || '—' }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Цифровые паспорта изделий -->
    <div class="space-y-6">
      <div class="flex items-center space-x-3 mb-6">
        <div class="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
        <h2 class="text-2xl font-bold text-slate-800">Цифровые паспорта изделий</h2>
      </div>

      <div v-for="(task, taskIndex) in order.tasks" :key="taskIndex"
           :id="'task-' + task.id"
           class="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

        <!-- Заголовок изделия (всегда видимый) -->
        <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-8 py-6 cursor-pointer hover:from-slate-700 hover:to-slate-600 transition-all"
             @click="toggleTask(taskIndex)">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <span class="text-white font-bold text-lg">{{ taskIndex + 1 }}</span>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white mb-1">
                  Изделие #{{ task.id }}
                </h3>
                <div class="flex items-center space-x-4 flex-wrap gap-y-2" @click.stop>
                  <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="getTaskStatusClass(task.status)">
                    {{ getStatusLabel(task.status) }}
                  </span>
                  <span v-if="getProductStatusBadge(task)"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                        :class="getProductStatusBadge(task).class">
                    {{ getProductStatusBadge(task).label }}
                  </span>
                  <!-- Контроль незакрытых случаев (ТЗ-7.10): по случаю нет ни одного
                       действующего документа качества. Подпись приходит с бэкенда готовой -->
                  <span v-if="getNonconformityFlags(task)?.needs_investigation_label"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                    {{ getNonconformityFlags(task).needs_investigation_label }}
                  </span>
                  <!-- Залежавшийся случай: движения по нему нет дольше недели -->
                  <span v-if="getNonconformityFlags(task)?.stale_label"
                        class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    {{ getNonconformityFlags(task).stale_label }}
                  </span>
                  <!-- Ошибочно остановленное изделие возвращается в работу без документов.
                       Возврат без документа качества — решение службы качества, поэтому
                       кнопка есть только у Инженера по качеству и Администратора. Директивы
                       режима чтения здесь нет намеренно: у Инженера по качеству «Производство»
                       по матрице только на чтение, а возврат — его действие -->
                  <button
                    v-if="task.stopped_at && canResumeWithoutDocument"
                    type="button"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="resumingTaskId === task.id"
                    @click.stop="openResumeModal(task)"
                  >
                    {{ resumingTaskId === task.id ? 'Возвращаем…' : 'Вернуть в работу' }}
                  </button>
                  <!-- Штатный возврат по решению службы качества: отдельное действие от ручного,
                       открывается согласованным документом и позволяет выбрать операцию -->
                  <button
                    v-if="routeReturnOptions[task.id]?.is_available"
                    v-can-write="productionModuleId"
                    type="button"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition"
                    @click.stop="openRouteReturnModal(task)"
                  >
                    Вернуть на маршрут
                  </button>
                  <!-- Окончательный брак: возврат закрыт, замена — единственный выход.
                       Открыта ли она, решает бэкенд (getReplacementOptions) -->
                  <button
                    v-if="replacementOptions[task.id]?.is_available"
                    v-can-write="productionModuleId"
                    type="button"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-rose-500 text-white hover:bg-rose-600 transition"
                    @click.stop="openReplacementModal(task)"
                  >
                    Заменить
                  </button>
                  <!-- Связь «забракованный экземпляр — замена» приходит с заказом в обе стороны
                       и переживает перезагрузку; подпись готова на бэкенде -->
                  <button
                    v-if="replacementLinksByTask[task.id]?.replaced_by"
                    type="button"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-rose-100 text-rose-800 hover:bg-rose-200 transition"
                    title="Перейти к паспорту замены"
                    @click.stop="scrollToTask(replacementLinksByTask[task.id].replaced_by.production_task_id)"
                  >
                    Заменено изделием {{ replacementLinksByTask[task.id].replaced_by.label }}
                  </button>
                  <button
                    v-if="replacementLinksByTask[task.id]?.replacement_of"
                    type="button"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition"
                    title="Перейти к паспорту забракованного изделия"
                    @click.stop="scrollToTask(replacementLinksByTask[task.id].replacement_of.production_task_id)"
                  >
                    Заведено взамен изделия {{ replacementLinksByTask[task.id].replacement_of.label }}
                  </button>
                  <div class="flex items-center gap-2 text-slate-300 text-sm">
                    <span class="text-slate-400">Серийный номер:</span>
                    <template v-if="editingSerialTaskId === task.id">
                      <input
                        v-model="serialEditDraft"
                        type="text"
                        class="px-2 py-1 rounded border border-slate-500 bg-slate-800 text-white text-sm min-w-[10rem] focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                        :disabled="serialSaving"
                        @keyup.enter="saveSerialNumber(task)"
                      />
                      <button
                        type="button"
                        class="text-blue-300 hover:text-blue-200 underline text-sm disabled:opacity-50"
                        :disabled="serialSaving"
                        @click="saveSerialNumber(task)"
                      >
                        Сохранить
                      </button>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-200 underline text-sm"
                        :disabled="serialSaving"
                        @click="cancelSerialEdit"
                      >
                        Отмена
                      </button>
                    </template>
                    <template v-else>
                      <span>{{ task.batch_products?.[0]?.serial_number || 'N/A' }}</span>
                      <a
                        v-if="task.batch_products?.[0]?.id"
                        v-can-write="productionModuleId"
                        href="#"
                        class="text-blue-300 hover:text-blue-200 underline text-sm ml-1"
                        @click.prevent="startSerialEdit(task)"
                      >
                        Изменить
                      </a>
                    </template>
                  </div>
                </div>
                <!-- Причина и время остановки изделия по несоответствию -->
                <div v-if="task.stopped_at" class="mt-2 text-sm text-red-200" @click.stop>
                  Остановлено {{ getDateTime(task.stopped_at) }}<span v-if="task.stopped_reason">: {{ task.stopped_reason }}</span>
                </div>
                <!-- Изделие уже выведено из маршрута заменой: условия действий по нему не
                     запрашиваем, поэтому объясняем отсутствие кнопок здесь -->
                <div v-if="finalDefectNotes[task.id]" class="mt-1 text-sm text-slate-300" @click.stop>
                  {{ finalDefectNotes[task.id] }}
                </div>
                <!-- Почему штатный возврат закрыт: текст приходит с бэкенда готовым -->
                <div v-if="routeReturnReasons[task.id]" class="mt-1 text-sm text-slate-300" @click.stop>
                  {{ routeReturnReasons[task.id] }}
                </div>
                <!-- Сбой загрузки условий возврата — не то же самое, что запрет:
                     показываем отличимое состояние и даём повторить -->
                <div v-if="routeReturnErrors[task.id]" class="mt-1 text-sm text-amber-200" @click.stop>
                  {{ routeReturnLoadErrorText }}
                  <button
                    type="button"
                    class="ml-1 underline hover:text-amber-100 disabled:opacity-50 disabled:no-underline"
                    :disabled="isOptionsRetrying('routeReturn', task.id)"
                    @click="retryRouteReturnOptions(task)"
                  >
                    {{ isOptionsRetrying('routeReturn', task.id) ? 'Загружаем…' : 'Повторить' }}
                  </button>
                </div>
                <!-- Почему замена закрыта: текст приходит с бэкенда готовым -->
                <div v-if="replacementReasons[task.id]" class="mt-1 text-sm text-slate-300" @click.stop>
                  Замена изделия: {{ replacementReasons[task.id] }}
                </div>
                <!-- Сбой загрузки условий замены: тот же принцип, что и с возвратом -->
                <div v-if="replacementErrors[task.id]" class="mt-1 text-sm text-amber-200" @click.stop>
                  {{ replacementLoadErrorText }}
                  <button
                    type="button"
                    class="ml-1 underline hover:text-amber-100 disabled:opacity-50 disabled:no-underline"
                    :disabled="isOptionsRetrying('replacement', task.id)"
                    @click="retryReplacementOptions(task)"
                  >
                    {{ isOptionsRetrying('replacement', task.id) ? 'Загружаем…' : 'Повторить' }}
                  </button>
                </div>
                <!-- Расследование по изделию: заводится автоматически при фиксации брака,
                     ведёт его специалист СМК на своём столе -->
                <div v-if="getActiveInvestigation(task)" class="mt-2 text-sm" @click.stop>
                  <a
                    v-if="getActiveInvestigation(task).link && isExternalLink(getActiveInvestigation(task).link)"
                    :href="getActiveInvestigation(task).link"
                    target="_blank"
                    rel="noopener"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-medium hover:bg-amber-200 transition"
                  >
                    Расследование несоответствия {{ getActiveInvestigation(task).number }} — {{ getActiveInvestigation(task).status_label }}
                  </a>
                  <router-link
                    v-else-if="getActiveInvestigation(task).link"
                    :to="getActiveInvestigation(task).link"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-medium hover:bg-amber-200 transition"
                  >
                    Расследование несоответствия {{ getActiveInvestigation(task).number }} — {{ getActiveInvestigation(task).status_label }}
                  </router-link>
                  <span v-else class="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">
                    Расследование несоответствия {{ getActiveInvestigation(task).number }} — {{ getActiveInvestigation(task).status_label }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-3">
              <div class="text-right text-white">
                <div class="text-sm opacity-75">Операций</div>
                <div class="text-lg font-bold">{{ task.operations.length }}</div>
              </div>
              <div class="text-white transition-transform duration-300"
                   :class="expandedTasks.includes(taskIndex) ? 'rotate-180' : ''">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Детали изделия (сворачиваемые) -->
        <div v-if="expandedTasks.includes(taskIndex)" class="p-8">
          <!-- Документы качества, связанные с изделием (свежие сверху) -->
          <div v-if="getQualityDocuments(task).length > 0" class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-rose-400 rounded-full mr-2"></div>
              Документы качества ({{ getQualityDocuments(task).length }})
            </h5>
            <div class="space-y-3">
              <div v-for="doc in getQualityDocuments(task)" :key="doc.key"
                   class="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
                <div class="min-w-0">
                  <div class="text-sm font-bold text-slate-800">
                    <a
                      v-if="doc.link && isExternalLink(doc.link)"
                      :href="doc.link"
                      target="_blank"
                      rel="noopener"
                      class="text-blue-600 hover:text-blue-700 underline"
                    >
                      {{ doc.type_label }} № {{ doc.number }}
                    </a>
                    <router-link
                      v-else-if="doc.link"
                      :to="doc.link"
                      class="text-blue-600 hover:text-blue-700 underline"
                    >
                      {{ doc.type_label }} № {{ doc.number }}
                    </router-link>
                    <span v-else>{{ doc.type_label }} № {{ doc.number }}</span>
                  </div>
                  <!-- Дата приходит уже человекочитаемой строкой (ДД.ММ.ГГГГ), повторно не форматируем -->
                  <div class="text-xs text-slate-500 mt-1">
                    от {{ doc.date || '—' }}
                    <span v-if="doc.origin_label"> · {{ doc.origin_label }}</span>
                  </div>
                </div>
                <span v-if="doc.status_label"
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
                  {{ doc.status_label }}
                </span>
              </div>
            </div>
          </div>
          <!-- Кнопки печати по шагам операций -->
          <div class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
              Документы для печати
            </h5>
            <div class="space-y-3">
              <div v-for="(operation, opIndex) in task.operations.slice().sort((a, b) => (a.operation?.sort || 0) - (b.operation?.sort || 0))" :key="opIndex">
                <div v-if="getPrintStepsFromOperation(operation).length > 0" class="bg-white rounded-lg border border-slate-200 p-4">
                  <div class="text-sm font-medium text-slate-600 mb-3">
                    {{ operation.operation?.name || 'Операция без названия' }}
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="(step, stepIndex) in getPrintStepsFromOperation(operation)"
                      :key="step.id"
                      @click.prevent="printOperationStep(step, task)"
                      :disabled="!task.batch_products?.length"
                      class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {{ stepIndex + 1 }}. {{ getStepDocumentNames(step) }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="getAllPrintStepsFromTask(task).length === 0" class="text-sm text-slate-500 italic">
                Нет шагов печати для данной задачи
              </div>
            </div>
          </div>
          <!-- Прикреплённые файлы (операции и шаги спецификации) -->
          <div v-if="(attachedPrintFilesByTask[task.id] || []).length > 0" class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-cyan-400 rounded-full mr-2"></div>
              Прикреплённые файлы
            </h5>
            <div class="space-y-3">
              <div v-for="group in (attachedPrintFilesByTask[task.id] || [])" :key="'attgrp-' + group.key"
                   class="bg-white rounded-lg border border-slate-200 p-4">
                <div class="text-sm font-medium text-slate-600 mb-3">{{ group.title }}</div>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="(file, fileIndex) in group.files"
                    :key="'attfile-' + file.id"
                    @click.prevent="printAttachedFile(file.id)"
                    class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {{ fileIndex + 1 }}. {{ file.name }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Упаковочные листы -->
          <div v-if="getDonePackagingOperations(task).length > 0" class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <div class="w-2 h-2 bg-indigo-400 rounded-full mr-2"></div>
              Упаковочные листы
            </h5>
            <div class="space-y-3">
              <div v-for="(operation, opIndex) in getDonePackagingOperations(task)" :key="'packing-' + opIndex">
                <div class="bg-white rounded-lg border border-slate-200 p-4">
                  <div class="text-sm font-medium text-slate-600 mb-3">
                    {{ operation.operation?.name || 'Операция без названия' }}
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      @click.prevent="printPackingDocument('order', task)"
                      class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Упаковочный лист заказа
                    </button>
                    <button
                      @click.prevent="printPackingDocument('place', task)"
                      class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Упаковочные листы по местам
                    </button>
                    <button
                      @click.prevent="printPackingDocument('labels', task)"
                      class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Транспортные этикетки
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Раскрывашка "Все использованные компоненты" — одна на task, над операциями -->
          <div v-if="task.task_products && task.task_products.length > 0" class="mb-6">
            <button
              @click="toggleOperationProducts(task.id)"
              class="flex items-center w-full text-left mb-4 group">
              <svg
                class="w-5 h-5 text-slate-500 mr-2 transition-transform duration-200"
                :class="{ 'rotate-90': expandedOperationProducts.includes(task.id) }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
              <h5 class="text-lg font-semibold text-slate-800 flex items-center group-hover:text-blue-600 transition-colors">
                <div class="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                Все использованные компоненты ({{ task.task_products.length }})
              </h5>
            </button>

            <div v-if="expandedOperationProducts.includes(task.id)">
              <TaskProductsTable
                :products="task.task_products"
                placeholder="Поиск по компонентам изделия"
              />
            </div>
          </div>

          <!-- Используемое ПО изделия: ключ активации вводится здесь, сборщик видит его на столе.
               Правка — только по кнопке «Изменить», сохранение — одной кнопкой за весь блок -->
          <div v-if="task.software_keys && task.software_keys.length > 0" class="mb-6 bg-white rounded-lg border border-slate-200 p-4">
            <div class="flex items-center justify-between mb-3">
              <h5 class="text-md font-semibold text-slate-700 flex items-center">
                <div class="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                Используемое ПО
              </h5>
              <div class="flex items-center gap-2">
                <template v-if="isEditingSoftwareKeys(task)">
                  <button
                    @click="cancelSoftwareKeysEdit(task)"
                    :disabled="savingSoftwareTaskId === task.id"
                    class="px-3 py-1 bg-white border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Отмена
                  </button>
                  <button
                    @click="saveSoftwareKeys(task)"
                    :disabled="savingSoftwareTaskId === task.id"
                    class="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50"
                  >
                    {{ savingSoftwareTaskId === task.id ? 'Сохранение...' : 'Сохранить' }}
                  </button>
                </template>
                <button
                  v-else-if="task.software_keys.some(software => software.is_editable !== false)"
                  @click="startSoftwareKeysEdit(task)"
                  class="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                >
                  Изменить
                </button>
              </div>
            </div>
            <table class="w-full border-collapse text-sm">
              <thead class="bg-slate-50">
                <tr>
                  <th class="px-3 py-2 text-left text-slate-600">Наименование</th>
                  <th class="px-3 py-2 text-left text-slate-600">Артикул</th>
                  <th class="px-3 py-2 text-left text-slate-600">Ключ активации</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="software in task.software_keys"
                  :key="'software-' + task.id + '-' + software.product_id"
                  class="border-b border-slate-100"
                >
                  <td class="px-3 py-2 font-medium text-slate-800">{{ software.name }}</td>
                  <td class="px-3 py-2 text-slate-600">{{ software.sku || '—' }}</td>
                  <td class="px-3 py-2">
                    <!-- После запуска заказа заполненный ключ не меняется: поля для него нет -->
                    <input
                      v-if="isEditingSoftwareKeys(task) && software.is_editable !== false"
                      v-model="softwareKeyDrafts[task.id][software.product_id]"
                      type="text"
                      maxlength="256"
                      placeholder="введите ключ активации"
                      :disabled="savingSoftwareTaskId === task.id"
                      class="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                    />
                    <template v-else>
                      <span v-if="software.activation_key" class="text-slate-700 break-all">{{ software.activation_key }}</span>
                      <span v-else class="text-slate-400">Ключ не указан</span>
                      <p v-if="software.is_editable === false" class="text-xs text-slate-500 mt-1">Заказ запущен: ключ изменить нельзя</p>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Выпуск продукта: последнее звено истории изделия. До Z-28 цифровой паспорт
               обрывался на упаковке, и решение службы качества в него не попадало -->
          <div v-if="getReleaseInfo(task)" class="mb-6">
            <h5 class="text-lg font-semibold text-slate-800 mb-3 flex items-center">
              <div class="w-2 h-2 bg-emerald-500 rounded-full mr-2"></div>
              Выпуск продукта
            </h5>
            <div class="bg-white rounded-lg border border-slate-200 p-4">
              <dl class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                <div>
                  <dt class="text-slate-500">Решение</dt>
                  <dd class="font-medium" :class="getReleaseInfo(task).statusClass">{{ getReleaseInfo(task).statusLabel }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Дата решения</dt>
                  <dd class="font-medium text-slate-800">{{ getReleaseInfo(task).decidedAt }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Акт заполнил</dt>
                  <dd class="font-medium text-slate-800">{{ getReleaseInfo(task).checkedBy }}</dd>
                </div>
                <div>
                  <dt class="text-slate-500">Выпуск согласовал</dt>
                  <dd class="font-medium text-slate-800">{{ getReleaseInfo(task).approvedBy }}</dd>
                </div>
              </dl>
              <p v-if="getReleaseInfo(task).comment" class="mt-3 text-sm text-slate-600">
                Комментарий: {{ getReleaseInfo(task).comment }}
              </p>
            </div>
          </div>

          <!-- Шаги спецификации -->
          <div>
            <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h5 class="text-lg font-semibold text-slate-800 flex items-center">
                <div class="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                Шаги спецификации
                <span class="ml-2 text-sm font-medium text-slate-500">
                  закрыто {{ getStepsProgress(task).done }} из {{ getStepsProgress(task).total }}
                </span>
                <span v-if="getOutOfOrderCount(task) > 0"
                      class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                      title="Шаги, начатые до завершения предыдущего по спецификации">
                  порядок нарушен: {{ getOutOfOrderCount(task) }}
                </span>
              </h5>
              <button
                @click="toggleAllOperations(task)"
                class="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                {{ areAllOperationsExpanded(task) ? 'Свернуть все' : 'Развернуть все' }}
              </button>
            </div>

            <!-- Прогресс по шагам -->
            <div class="h-2 w-full bg-slate-200 rounded-full overflow-hidden mb-4">
              <div class="h-full bg-green-500 rounded-full transition-all duration-300"
                   :style="{ width: getStepsProgress(task).percent + '%' }"></div>
            </div>

            <!-- Последовательность шагов: свёрнутые строки + раскрытие подробностей -->
            <div class="space-y-2">
              <div v-for="(operation, opIndex) in getSortedOperations(task)"
                   :key="operation.id"
                   :id="'operation-' + operation.id"
                   class="bg-white rounded-xl border border-slate-200 overflow-hidden">

                <!-- Свёрнутая строка шага -->
                <div class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                     @click="toggleOperation(operation.id)">
                  <div class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                       :class="[
                         getOperationBulletClass(operation.status),
                         isOutOfOrder(operation) ? 'ring-2 ring-amber-400 ring-offset-1' : '',
                       ]"
                       :title="'Шаг ' + (opIndex + 1) + ' по спецификации'">
                    {{ opIndex + 1 }}
                  </div>

                  <div class="font-semibold text-slate-800 truncate">
                    {{ operation.operation?.name || 'Операция без названия' }}
                  </div>

                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                        :class="getOperationStatusClass(operation.status)">
                    {{ getStatusLabel(operation.status) }}
                  </span>

                  <span v-if="isOutOfOrder(operation)"
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0"
                        :title="getOutOfOrderReason(operation)">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                    </svg>
                    Вне очереди
                  </span>

                  <div class="ml-auto hidden lg:flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                    <span v-if="getWorkCenterLabel(operation)">{{ getWorkCenterLabel(operation) }}</span>
                    <span v-if="operation.user">{{ operation.user.first_name }} {{ operation.user.last_name }}</span>
                    <span v-if="operation.fact_end_datetime">{{ getDateTime(operation.fact_end_datetime) }}</span>
                  </div>

                  <svg class="w-5 h-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ml-auto lg:ml-0"
                       :class="{ 'rotate-90': expandedOperations.includes(operation.id) }"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>

                <!-- Подробности шага -->
                <div v-if="expandedOperations.includes(operation.id)"
                     class="border-t border-slate-200 bg-slate-50 p-6">
                  <!-- Нарушение порядка шагов из спецификации -->
                  <div v-if="isOutOfOrder(operation)"
                       class="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path>
                    </svg>
                    <span>{{ getOutOfOrderReason(operation) }}</span>
                  </div>

                  <!-- Временные рамки -->
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div class="flex items-center space-x-2">
                      <div class="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <div>
                        <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Начало</div>
                        <div class="text-sm font-semibold text-slate-700">
                          {{ operation.fact_start_datetime ? getDateTime(operation.fact_start_datetime) : 'Не начато' }}
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center space-x-2">
                      <div class="w-2 h-2 bg-green-400 rounded-full"></div>
                      <div>
                        <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Завершение</div>
                        <div class="text-sm font-semibold text-slate-700">
                          {{ operation.fact_end_datetime ? getDateTime(operation.fact_end_datetime) : 'Не завершено' }}
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center space-x-2">
                      <div class="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <div>
                        <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold">Длительность</div>
                        <div class="text-sm font-semibold text-slate-700">
                          {{ getDuration(operation.fact_start_datetime, operation.fact_end_datetime) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Рабочий центр и ответственный -->
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div class="bg-white rounded-lg p-4 border border-slate-200">
                      <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Рабочий центр</div>
                      <div class="text-sm font-semibold text-slate-800" v-if="getWorkCenterLabel(operation)">
                        {{ getWorkCenterLabel(operation) }}
                      </div>
                      <div class="text-sm text-slate-500 italic" v-else>
                        Не назначен
                      </div>
                    </div>
                    <div class="bg-white rounded-lg p-4 border border-slate-200">
                      <div class="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Ответственный</div>
                      <div class="text-sm font-semibold text-slate-800" v-if="operation.user">
                        {{ operation.user.first_name }} {{ operation.user.last_name }}
                      </div>
                      <div class="text-sm text-slate-500 italic" v-else>
                        Не назначен
                      </div>
                    </div>
                  </div>

                  <!-- Требуемые компоненты (планирование серийной продукции) -->
                  <div v-if="isPickingOperation(operation) && getRequiredComponents(operation).length > 0" class="mb-6 bg-white rounded-lg border border-slate-200 p-4">
                    <div class="flex items-center justify-between mb-3">
                      <h5 class="text-md font-semibold text-slate-700 flex items-center">
                        <div class="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                        Требуемые компоненты
                      </h5>
                      <div class="flex items-center gap-2">
                        <button
                          v-if="canPrintTask(operation)"
                          @click.prevent="printPickingTask(operation)"
                          class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          Задание на комплектование
                        </button>
                        <button
                          v-if="canPrintTask(operation)"
                          @click.prevent="printMovementTask(operation)"
                          class="inline-flex items-center px-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition ease-in-out duration-150"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          Перемещение
                        </button>
                        <button
                          v-if="operation.status !== 'done' && operation.status !== 'work'"
                          v-can-write="productionModuleId"
                          @click="openComponentModal(operation)"
                          class="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                        >
                          Указать продукцию
                        </button>
                      </div>
                    </div>
                    <table class="w-full border-collapse text-sm">
                      <thead class="bg-slate-50">
                        <tr>
                          <th class="px-3 py-2 text-left text-slate-600">Наименование</th>
                          <!-- Столбец «Требуется» убран (Z-23): норма по спецификации
                               уже стоит в счётчике «запланировано / требуется» справа -->
                          <th class="px-3 py-2 text-left text-slate-600">Взято фактически</th>
                          <th class="px-3 py-2 text-left text-slate-600">Запланировано</th>
                          <th class="px-3 py-2 text-left text-slate-600"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="comp in getRequiredComponents(operation)" :key="comp.product_id" class="border-b border-slate-100">
                          <td class="px-3 py-2 font-medium text-slate-800">{{ comp.product_name }}</td>
                          <td class="px-3 py-2" :class="comp.has_overpick ? 'text-amber-700 font-semibold' : 'text-slate-600'">
                            {{ formatQuantity(comp.picked_quantity) }}
                            <span
                              v-if="comp.has_overpick"
                              class="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold whitespace-nowrap"
                            >Перерасход</span>
                          </td>
                          <td class="px-3 py-2">
                            <div v-if="comp.planned_serials.length > 0" class="space-y-1">
                              <div v-for="(planned, pIdx) in comp.planned_serials" :key="pIdx" class="flex items-center space-x-2">
                                <span class="text-sm" :class="planned.is_overpick ? 'text-amber-700 font-semibold' : 'text-slate-700'">{{ planned.serial_number || 'Без S/N' }}</span>
                                <button
                                  v-if="operation.status !== 'done' && operation.status !== 'work' && !planned.is_picked"
                                  v-can-write="productionModuleId"
                                  @click="deletePlannedComponent(operation, comp.product_id, planned.task_product_id)"
                                  class="text-red-500 hover:text-red-700 text-xs"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                            <span v-else class="text-slate-400">—</span>
                          </td>
                          <td class="px-3 py-2 text-right text-slate-500">
                            {{ comp.planned_serials.length }} / {{ comp.required_quantity }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Компоненты по шагам операции -->
                  <template v-for="(step, stepIndex) in (operation.steps || [])" :key="'step-' + stepIndex">
                    <div v-if="getStepTaskProducts(step, operation).length > 0" class="mb-6">
                      <h5 class="text-md font-semibold text-slate-700 mb-3 flex items-center">
                        <div class="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                        {{ step.operation_step?.name || ('Шаг ' + (stepIndex + 1)) }}
                      </h5>
                      <TaskProductsTable
                        :products="getStepTaskProducts(step, operation)"
                        placeholder="Поиск по компонентам шага"
                      />
                    </div>
                  </template>

                  <!-- Фотографии -->
                  <div v-if="operation.photos && operation.photos.length > 0" class="mb-4">
                    <h5 class="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                      <div class="w-2 h-2 bg-pink-400 rounded-full mr-2"></div>
                      Фотофиксация выполнения
                    </h5>

                    <div v-if="operation.photos && operation.photos.length > 0"
                         class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      <div v-for="(photo, photoIndex) in operation.photos" :key="photoIndex"
                           class="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                           @click="openPhotosModal(operation.id, photoIndex)">
                        <img :src="photo.path" alt="Фото работы"
                             class="w-full h-20 object-cover"/>
                        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                          <div class="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                            </svg>
                          </div>
                        </div>
                        <!-- Индикатор номера фото -->
                        <div class="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                          {{ photoIndex + 1 }}
                        </div>
                      </div>
                    </div>

                    <div v-else
                         class="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <svg class="mx-auto h-12 w-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <p class="text-slate-500 font-medium">Фотографии не добавлены</p>
                      <button @click="openPhotosModal(operation.id, 0)"
                              class="mt-2 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                        Посмотреть фотографии
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно для просмотра фотографий -->
    <div v-if="isPhotosModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
         @click="closePhotosModal">
      <div class="bg-white rounded-2xl shadow-2xl w-[95%] max-w-6xl max-h-[90vh] overflow-hidden"
           @click.stop>

        <!-- Заголовок модального окна -->
        <div class="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
          <div class="flex justify-between items-center">
            <div>
              <h2 class="text-xl font-bold text-white">Фотофиксация операции</h2>
              <p class="text-slate-300 text-sm" v-if="operationPhotos.length > 0">
                Фото {{ currentPhotoIndex + 1 }} из {{ operationPhotos.length }}
              </p>
            </div>
            <button @click="closePhotosModal"
                    class="text-slate-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6">
          <!-- Загрузка -->
          <div v-if="photosLoading" class="flex justify-center items-center h-96">
            <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          </div>

          <!-- Просмотр фотографий -->
          <div v-else-if="operationPhotos.length > 0" class="space-y-6">
            <!-- Основное фото -->
            <div class="relative bg-slate-100 rounded-xl overflow-hidden">
              <img :src="operationPhotos[currentPhotoIndex].path"
                   alt="Фото работы"
                   class="w-full max-h-96 object-contain mx-auto"/>

              <!-- Навигация по фото -->
              <button v-if="operationPhotos.length > 1"
                      @click="previousPhoto"
                      class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <button v-if="operationPhotos.length > 1"
                      @click="nextPhoto"
                      class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>

            <!-- Информация о фото -->
            <div class="bg-slate-50 rounded-lg p-4">
              <div class="flex items-center justify-center">
                <div class="text-center">
                  <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                    Дата и время съемки
                  </div>
                  <div class="text-lg font-semibold text-slate-800">
                    {{ formatPhotoDateTime(operationPhotos[currentPhotoIndex].created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Превью всех фото -->
            <div v-if="operationPhotos.length > 1" class="space-y-2">
              <div class="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                Все фотографии ({{ operationPhotos.length }})
              </div>
              <div class="flex space-x-2 overflow-x-auto pb-2">
                <div v-for="(photo, index) in operationPhotos" :key="index"
                     class="flex-shrink-0 cursor-pointer rounded-lg overflow-hidden border-2 transition-all"
                     :class="index === currentPhotoIndex ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-slate-300'"
                     @click="currentPhotoIndex = index">
                  <img :src="photo.path" alt="Превью" class="w-16 h-16 object-cover"/>
                </div>
              </div>
            </div>
          </div>

          <!-- Нет фотографий -->
          <div v-else class="text-center py-12">
            <svg class="mx-auto h-16 w-16 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <p class="text-lg font-medium text-slate-700 mb-2">Фотографии не найдены</p>
            <p class="text-slate-500">Для данной операции фотофиксация не проводилась</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Загрузка -->
  <div v-else class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
      <p class="text-xl font-semibold text-slate-700">Загрузка цифровых паспортов...</p>
      <p class="text-slate-500 mt-2">Получаем информацию о производстве изделий</p>
    </div>
  </div>
  
  <!-- Модальное окно планирования компонентов -->
  <ComponentPlanningModal ref="componentModal" @saved="onComponentSaved" />

  <!-- Подтверждение возврата остановленного изделия в работу -->
  <div v-if="resumeModal.show"
       class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
       @click.self="closeResumeModal">
    <div class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6">
      <h3 class="text-lg font-bold text-slate-800 mb-3">Вернуть изделие в работу?</h3>
      <p class="text-sm text-slate-600 mb-2">
        Изделие #{{ resumeModal.taskId }} снова встанет в очередь на ту же операцию. Документы качества при этом не создаются.
      </p>
      <p v-if="resumeModal.reason" class="text-sm text-slate-500 mb-4">
        Причина остановки: {{ resumeModal.reason }}
      </p>
      <!-- Причина возврата обязательна и уходит в историю задания (Z-09) -->
      <label class="block text-sm font-medium text-slate-700 mb-1">
        Причина возврата в работу <span class="text-red-600">*</span>
      </label>
      <textarea
        v-model="resumeModal.resumeReason"
        rows="3"
        maxlength="2000"
        placeholder="Почему изделие возвращается в работу"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent mb-1"
      ></textarea>
      <p class="text-xs text-slate-500 mb-4">{{ (resumeModal.resumeReason || '').length }} из 2000 символов</p>
      <p v-if="resumeModal.error" class="text-sm text-red-600 mb-4 whitespace-pre-line break-words">
        {{ resumeModal.error }}
      </p>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
          :disabled="resumingTaskId !== null"
          @click="closeResumeModal"
        >
          Отмена
        </button>
        <button
          type="button"
          class="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50"
          :disabled="resumingTaskId !== null || !(resumeModal.resumeReason || '').trim()"
          @click="resumeProduction"
        >
          {{ resumingTaskId !== null ? 'Возвращаем…' : 'Вернуть в работу' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Штатный возврат на маршрут по согласованному документу качества -->
  <RouteReturnModal
    :show="routeReturnModal.show"
    :batch-product-id="routeReturnModal.batchProductId"
    :nonconformity-case-id="routeReturnModal.nonconformityCaseId"
    :initial-options="routeReturnModal.options"
    @close="closeRouteReturnModal"
    @returned="onReturnedToRoute"
  />

  <!-- Замена окончательно забракованного изделия: до нажатия видно, с какой операции
       пойдёт замена и какие операции ей достанутся, после — новый серийный номер рядом со старым -->
  <div v-if="replacementModal.show"
       class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
       @click.self="closeReplacementModal">
    <div class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-lg p-6 max-h-[90vh] overflow-y-auto">
      <h3 class="text-lg font-bold text-gray-800 mb-1">Заменить забракованное изделие</h3>
      <p class="text-sm text-gray-500 mb-4">Изделие {{ serialLabel(replacementModal.options?.serial_number) }}</p>

      <!-- Итог замены: сводку показываем как есть, её собрал бэкенд -->
      <template v-if="replacementModal.result">
        <p class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">
          {{ replacementModal.result.summary || 'Замена изделия заведена' }}
        </p>

        <!-- Оба номера рядом: номер забракованного экземпляра остаётся занятым навсегда -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <div class="text-xs uppercase tracking-wide font-semibold text-rose-700 mb-1">Забраковано</div>
            <div class="text-sm font-bold text-rose-900">
              {{ serialLabel(replacementModal.result.replaced_serial_number) }}
            </div>
          </div>
          <div class="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div class="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">Замена</div>
            <div class="text-sm font-bold text-emerald-900">
              {{ serialLabel(replacementModal.result.serial_number) }}
            </div>
          </div>
        </div>

        <div v-if="(replacementModal.result.operations || []).length > 0" class="mb-4">
          <div class="text-sm font-medium text-gray-700 mb-2">
            Операции замены. Всего: {{ replacementModal.result.operations.length }}
          </div>
          <ul class="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <li v-for="operation in replacementModal.result.operations" :key="'replacement-done-' + operation.id"
                class="px-3 py-2">
              <div class="text-sm text-gray-800">{{ operationTitle(operation) }}</div>
              <div class="text-xs text-gray-500">
                {{ operation.workshop_post_name || 'Рабочий центр не указан' }} · {{ operation.status_label }}
              </div>
            </li>
          </ul>
        </div>

        <div class="flex justify-end">
          <button
            type="button"
            class="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
            @click="closeReplacementModal"
          >
            Закрыть
          </button>
        </div>
      </template>

      <template v-else>
        <!-- Документ, открывший замену -->
        <div v-if="replacementDocument" class="mb-4 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <div class="font-medium">{{ replacementDocument.title }}</div>
          <div v-if="replacementDocument.product_action_label" class="text-gray-500 mt-0.5">
            Решение по продукту: {{ replacementDocument.product_action_label }}
          </div>
        </div>

        <label class="block text-sm font-medium text-gray-700 mb-1">
          Операция, с которой пойдёт замена
        </label>
        <div class="v-select-filter" style="width: 100%; flex: none;">
          <v-select
            v-model="replacementModal.selectedOperationId"
            :options="replacementOperations"
            :reduce="operation => operation.id"
            :get-option-label="replacementOperationLabel"
            :clearable="true"
            :disabled="replacementModal.submitting"
            placeholder="Весь маршрут с начала"
          >
            <template #option="operation">
              <div class="py-0.5">
                <div class="font-medium text-gray-800">{{ operationTitle(operation) }}</div>
                <div class="text-xs text-gray-500">
                  {{ operation.workshop_post_name || 'Рабочий центр не указан' }} · {{ operation.status_label }}
                </div>
              </div>
            </template>
          </v-select>
        </div>

        <p v-if="replacementModal.options?.from_operation_id" class="text-xs text-gray-500 mt-2">
          По умолчанию подставлена операция, на которой зафиксирован брак.
        </p>
        <p v-else class="text-xs text-amber-700 mt-2">
          Брак зафиксирован не на операции — без выбора замена пройдёт маршрут целиком.
        </p>

        <!-- Что именно произойдёт: набор операций будущей замены -->
        <div class="mt-4">
          <div class="text-sm font-medium text-gray-700 mb-2">
            Замена пройдёт эти операции. Всего: {{ replacementPreviewOperations.length }}
          </div>
          <ul v-if="replacementPreviewOperations.length > 0"
              class="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <li v-for="operation in replacementPreviewOperations" :key="'replacement-preview-' + operation.id"
                class="px-3 py-2"
                :class="operation.id === replacementModal.selectedOperationId ? 'bg-rose-50' : ''">
              <div class="text-sm text-gray-800">
                {{ operationTitle(operation) }}
                <span v-if="operation.id === replacementModal.selectedOperationId"
                      class="ml-1 text-xs font-semibold text-rose-700">
                  — операция, на которой был брак
                </span>
              </div>
              <div class="text-xs text-gray-500">
                {{ operation.workshop_post_name || 'Рабочий центр не указан' }}
              </div>
            </li>
          </ul>
          <p v-else class="text-sm text-gray-700">
            Операций, которые можно перенести в замену, у изделия нет.
          </p>
          <p class="text-xs text-gray-500 mt-2">
            Замена — отдельное изделие с новым серийным номером; номер забракованного экземпляра
            остаётся занятым навсегда. Операции до выбранной не повторяются, комплектование замена
            проходит заново — часть материалов переставляется с забракованного изделия.
            Забракованный экземпляр остаётся в заказе со своими документами.
          </p>
        </div>

        <p v-if="replacementModal.error" class="text-sm text-red-600 mt-3 whitespace-pre-line break-words">
          {{ replacementModal.error }}
        </p>

        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            :disabled="replacementModal.submitting"
            @click="closeReplacementModal"
          >
            Отмена
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50"
            :disabled="replacementModal.submitting || replacementPreviewOperations.length === 0"
            @click="submitReplacement"
          >
            {{ replacementModal.submitting ? 'Заводим замену…' : 'Заменить изделие' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from "moment/moment.js";
import ComponentPlanningModal from '../../components/production/ComponentPlanningModal.vue';
import TaskProductsTable from '../../components/production/TaskProductsTable.vue';
import TrashIcon from '../../components/icons/TrashIcon.vue';
import RouteReturnModal from '@/components/smkDesk/RouteReturnModal.vue';
import { useNotificationAutoRead } from '@/composables/useNotificationAutoRead';
import { useAuthStore } from '@/stores/auth';

// Ручной возврат в работу идёт без документа качества, поэтому доступен только службе
// качества и администратору. Тот же список режет бэкенд (ResumeProductionTaskRequest),
// здесь он только прячет кнопку
const RESUME_WITHOUT_DOCUMENT_ROLES = ['Администратор', 'Инженер по качеству'];

// Тексты сбоя загрузки условий действий по изделию. Сообщение бэкенда на экран не
// выносим: у 500 там технический текст, а пользователю нужно понять только то, что
// это сбой, а не запрет, и что загрузку можно повторить. Подробности — в консоль.
const ROUTE_RETURN_LOAD_ERROR = 'Не удалось загрузить условия возврата на маршрут.';
const REPLACEMENT_LOAD_ERROR = 'Не удалось загрузить условия замены изделия.';

// Окончательный брак ставится только заведением замены, поэтому по такому изделию
// и возврат на маршрут, и повторная замена закрыты заведомо — условия не запрашиваем
const FINAL_DEFECT_STATUS = 'defect';

export default {
  components: {
    ComponentPlanningModal,
    TaskProductsTable,
    TrashIcon,
    RouteReturnModal
  },
  data() {
    return {
      order: null,
      loading: false,
      isPhotosModalVisible: false,
      operationPhotos: [],
      photosLoading: false,
      currentOperationId: null,
      currentPhotoIndex: 0,
      expandedTasks: [], // Массив для хранения ID развернутых изделий
      expandedOperationProducts: [], // Массив ID операций с раскрытым списком всех компонентов
      softwareKeyDrafts: {}, // Вводимые ключи блока «Используемое ПО» в режиме правки: { изделие: { ПО: ключ } }
      savingSoftwareTaskId: null, // Изделие, ключи которого сейчас сохраняются
      expandedOperations: [], // Массив ID шагов спецификации (операций) с раскрытыми подробностями
      editingSerialTaskId: null,
      serialEditDraft: '',
      serialSaving: false,
      resumingTaskId: null, // Задание, которое сейчас возвращается в работу
      resumeModal: { show: false, taskId: null, reason: '', error: '' }, // Подтверждение возврата в работу
      // Условия штатного возврата на маршрут по остановленным изделиям: ключ — id задания
      routeReturnOptions: {},
      routeReturnModal: { show: false, taskId: null, batchProductId: null, nonconformityCaseId: null, options: null },
      // Задания, по которым условия возврата не загрузились: ключ — id задания.
      // Отдельно от routeReturnOptions, чтобы сбой не выглядел как штатный запрет
      routeReturnErrors: {},
      // Условия замены забракованных изделий заказа: ключ — id задания
      replacementOptions: {},
      // Задания, по которым условия замены не загрузились: ключ — id задания
      replacementErrors: {},
      // Повторные загрузки условий, идущие прямо сейчас: ключ — «вид действия-id задания»
      optionsRetrying: {},
      replacementModal: {
        show: false,
        taskId: null,
        options: null,
        selectedOperationId: null,
        submitting: false,
        error: '',
        result: null,
      },
      taskData: {
        product_name: 'WATO EX-65',
        batch_product_id: 123,
        // другие поля task_data
      }
    };
  },
  computed: {
    // Виден ли ручной возврат в работу: роль пользователя приходит строкой (UserResource)
    canResumeWithoutDocument() {
      return RESUME_WITHOUT_DOCUMENT_ROLES.includes(useAuthStore().user?.role);
    },
    // Карточка заказа открыта по id заказа, id модуля в маршруте нет:
    // берём из query (переход из списка производства) либо из модулей пользователя по ключу production
    productionModuleId() {
      const fromRoute = this.$route.query.moduleId || this.$route.params.moduleId;
      if (fromRoute) return fromRoute;
      const modules = useAuthStore().user?.modules || [];
      return modules.find(m => m.key === 'production')?.id ?? null;
    },
    documents() {
      const docsMap = new Map();

      if (!this.order || !this.order.tasks) return [];

      this.order.tasks.forEach(task => {
        task.operations?.forEach(op => {
          op.operation?.steps?.forEach(step => {
            step.documents?.forEach(doc => {
              if (!docsMap.has(doc.id)) {
                docsMap.set(doc.id, doc);
              }
            });
          });
        });
      });

      return Array.from(docsMap.values());
    },
    // Шаги, выполнявшиеся вне порядка спецификации: ключ — id операции задания,
    // значение — название предыдущего по sort шага, который на тот момент не был закрыт.
    // Контроль последовательности (ProductionTaskOperation::isReadyByOrder) появился
    // не сразу, поэтому в старых заданиях такие расхождения встречаются
    outOfOrderOperations() {
      const map = {};
      if (!this.order || !this.order.tasks) return map;

      this.order.tasks.forEach(task => {
        const sorted = this.getSortedOperations(task);

        sorted.forEach((operation, index) => {
          if (!operation.fact_start_datetime) return;

          const start = moment(operation.fact_start_datetime);
          const blocking = sorted.slice(0, index).find(previous => {
            return !previous.fact_end_datetime
              || moment(previous.fact_end_datetime).isAfter(start);
          });

          if (blocking) {
            map[operation.id] = blocking.operation?.name || 'предыдущий шаг';
          }
        });
      });

      return map;
    },
    // Почему штатный возврат на маршрут закрыт, по заданиям (task.id).
    // Текст причины приходит с бэкенда готовым — здесь только раскладываем по заданиям.
    routeReturnReasons() {
      const map = {};
      Object.entries(this.routeReturnOptions).forEach(([taskId, options]) => {
        if (options && !options.is_available && options.reason) {
          map[taskId] = options.reason;
        }
      });
      return map;
    },
    // Почему замена изделия закрыта, по заданиям (task.id).
    // Текст причины приходит с бэкенда готовым — здесь только раскладываем по заданиям.
    replacementReasons() {
      const map = {};
      Object.entries(this.replacementOptions).forEach(([taskId, options]) => {
        if (options && !options.is_available && options.reason) {
          map[taskId] = options.reason;
        }
      });
      return map;
    },
    routeReturnLoadErrorText() {
      return ROUTE_RETURN_LOAD_ERROR;
    },
    replacementLoadErrorText() {
      return REPLACEMENT_LOAD_ERROR;
    },
    /**
     * Задания, по которым вообще имеет смысл спрашивать условия действий.
     *
     * И возврат на маршрут, и замена возможны только у остановленного изделия, а изделие
     * с окончательным браком выведено из маршрута заменой навсегда: по нему оба действия
     * закрыты заведомо. Забракованные экземпляры из заказа не исчезают (ТЗ-7.5), поэтому
     * без этого отсечения число запросов росло бы с каждой заменой.
     */
    actionOptionCandidates() {
      return (this.order?.tasks || []).filter(task => {
        const batchProduct = task.batch_products?.[0];
        if (!task.stopped_at || !batchProduct?.id) return false;
        if (batchProduct.receipt_status === FINAL_DEFECT_STATUS) return false;
        // Замена уже заведена — связь приходит с заказом, спрашивать бэкенд незачем
        return !batchProduct.replaced_by;
      });
    },
    // Изделия, выведенные из маршрута заменой: кнопок по ним не будет никогда,
    // поэтому отсутствие действий объясняем текстом, не тратя запрос
    finalDefectNotes() {
      const map = {};
      (this.order?.tasks || []).forEach(task => {
        const batchProduct = task.batch_products?.[0];
        if (!batchProduct) return;
        if (batchProduct.receipt_status !== FINAL_DEFECT_STATUS && !batchProduct.replaced_by) return;
        map[task.id] = 'Окончательный брак: изделие выведено из маршрута заменой — '
          + 'возврат на маршрут и повторная замена по нему закрыты.';
      });
      return map;
    },
    // Документ, открывший замену (согласованное расследование с решением «Брак продукта»)
    replacementDocument() {
      return this.replacementModal.options?.document || null;
    },
    // Операции забракованного задания в порядке маршрута — выбор точки, с которой пойдёт замена
    replacementOperations() {
      const list = this.replacementModal.options?.operations || [];
      return [...list].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    },
    replacementSelectedOperation() {
      const id = this.replacementModal.selectedOperationId;
      return this.replacementOperations.find(operation => operation.id === id) || null;
    },
    // Набор операций будущей замены.
    // Пока выбор не трогали — показываем расчёт бэкенда; после ручного выбора считаем
    // по тому же правилу (комплектование плюс всё, начиная с выбранной операции),
    // иначе предпросмотр отставал бы от выбора и обещал не то, что произойдёт.
    replacementPreviewOperations() {
      const options = this.replacementModal.options;
      if (!options) return [];

      const selectedId = this.replacementModal.selectedOperationId ?? null;
      const defaultId = options.from_operation_id ?? null;

      if (selectedId === defaultId) {
        return [...(options.replacement_operations || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      }

      // Операция не выбрана — замена проходит маршрут целиком
      if (selectedId === null) return this.replacementOperations;

      const selected = this.replacementSelectedOperation;
      if (!selected) return [];

      const fromSort = selected.sort || 0;
      return this.replacementOperations.filter(operation => operation.id === selected.id
        || (operation.sort || 0) >= fromSort
        || this.isPickingOperationById(operation.id));
    },
    // Связи цикла замен по заданиям (task.id): чем изделие заменено и взамен чего заведено.
    // Обе стороны приходят с заказом готовыми подписями — у каждого звена цепочки своя
    // пара ссылок, поэтому ничего не схлопываем и id на экран не выводим.
    replacementLinksByTask() {
      const map = {};
      (this.order?.tasks || []).forEach(task => {
        const batchProduct = task.batch_products?.[0];
        if (!batchProduct) return;
        if (batchProduct.replaced_by || batchProduct.replacement_of) {
          map[task.id] = {
            replaced_by: batchProduct.replaced_by || null,
            replacement_of: batchProduct.replacement_of || null,
          };
        }
      });
      return map;
    },
    // Прикреплённые для печати файлы, сгруппированные по заданию (task.id).
    // Computed, чтобы не пересчитывать дважды за рендер (условие + список).
    attachedPrintFilesByTask() {
      const map = {};
      if (!this.order || !this.order.tasks) return map;
      this.order.tasks.forEach(task => {
        map[task.id] = this.getAttachedPrintFilesFromTask(task);
      });
      return map;
    },
    /**
     * Даты заказа для шапки: две группы — план и факт, по два значения в каждой.
     * Пустая дата возвращается как null, шаблон рисует прочерк.
     */
    headerDateGroups() {
      const format = (value) => (value ? this.getDateTime(value) : null);

      return [
        {
          key: 'plan',
          title: 'План',
          iconClass: 'bg-indigo-100 text-indigo-600',
          iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
          rows: [
            { key: 'plan_start', label: 'Начало', value: format(this.order?.plan_start_datetime) },
            { key: 'plan_end', label: 'Завершение', value: format(this.order?.plan_end_datetime) },
          ],
        },
        {
          key: 'fact',
          title: 'Факт',
          iconClass: 'bg-emerald-100 text-emerald-600',
          iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
          rows: [
            { key: 'fact_start', label: 'Начало', value: format(this.order?.fact_start_datetime) },
            { key: 'fact_end', label: 'Завершение', value: format(this.order?.fact_end_datetime) },
          ],
        },
      ];
    }
  },
  methods: {
    goBackToTasks() {
      const moduleId = this.$route.query.moduleId || this.$route.params.moduleId;
      if (moduleId) {
        const query = { ...this.$route.query };
        delete query.moduleId;
        this.$router.push({
          name: 'Production',
          params: { id: moduleId },
          query
        });
      } else {
        this.$router.back();
      }
    },
    async fetchOrder(orderId) {
      try {
        this.loading = true;
        const response = await axios.post('/api/productionOrder/getProductionOrder', {id: orderId});
        this.order = response.data.data;
        // Ключи пришли с сервера заново — незавершённая правка блока «Используемое ПО» устарела
        this.softwareKeyDrafts = {};

        // Добавляем фотографии к каждой операции
        if (this.order && this.order.tasks) {
          for (const task of this.order.tasks) {
            for (const operation of task.operations) {
                await this.fetchOperationPhotos(operation);
            }
          }
        }

        // Условия возврата и замены не зависят друг от друга — грузим параллельно
        await Promise.all([this.loadRouteReturnOptions(), this.loadReplacementOptions()]);
      } catch (error) {
        console.error('Ошибка при загрузке заказа:', error);
      } finally {
        this.loading = false;
      }
    },
    getTaskComponents(task) {
      const componentsMap = new Map();

      task.operations.forEach(operation => {
        operation.steps.forEach(step => {
          step.operation_step?.components?.forEach(component => {
            const key = `${component.product?.id}_${component.count}`;
            if (!componentsMap.has(key)) {
              componentsMap.set(key, {
                id: component.id,
                product: {
                  id: component.product?.id || 0,
                  name: component.product?.name || 'Не указано'
                },
                count: component.count || 1
              });
            }
          });
        });
      });

      return Array.from(componentsMap.values());
    },

    // Требуемые компоненты операции: строка на каждый компонент шага комплектования —
    // и штучный, и количественный. Рядом с потребностью по спецификации показывается
    // фактически взятое; строки, где взяли больше спецификации, помечаются.
    getRequiredComponents(operation) {
      // Компоненты из ВСЕХ шагов комплектования (type = picking) операции.
      // Если picking-шагов несколько (напр. после копирования шага) — собираем по всем,
      // чтобы не зависеть от недетерминированного «первого» (тот же класс бага AND-184).
      const rows = [];
      (operation.steps || [])
        .filter(s => s.operation_step?.type === 'picking')
        .forEach((pickingStep, stepIdx) => {
          pickingStep.operation_step?.components?.forEach(component => {
            if (!component.product?.id) return;
            rows.push({
              row_key: `${pickingStep.id ?? stepIdx}-${component.id}`,
              component_id: component.id,
              product_id: component.product.id,
              product_name: component.product.name,
              product: component.product,
              is_single: !!component.is_single,
              required_quantity: Number(component.count) || 0,
              planned_serials: [],
              picked_quantity: 0,
              has_overpick: false,
            });
          });
        });

      if (rows.length === 0) return [];

      this.fillRequiredComponentsFact(operation, rows);

      return rows;
    },

    // Раскладывает запланированное и взятое по строкам компонентов.
    // Взятое с привязкой к компоненту шага ложится в свою строку; взятое раньше,
    // когда привязки ещё не было, относится к первой строке того же продукта —
    // иначе старые комплектования перестали бы отображаться вовсе.
    fillRequiredComponentsFact(operation, rows) {
      const rowsByComponent = new Map();
      rows.forEach(row => {
        if (row.component_id && !rowsByComponent.has(row.component_id)) {
          rowsByComponent.set(row.component_id, row);
        }
      });

      (operation.task_products || []).forEach(taskProduct => {
        // task_products приходят по всей задаче — позиции чужих операций отсеиваем
        if (taskProduct.operation_id && operation.id && taskProduct.operation_id !== operation.id) return;

        const productId = taskProduct.product_id || taskProduct.storehouse_product?.product_id;
        const row = rowsByComponent.get(taskProduct.operation_step_component_id)
          || rows.find(r => r.product_id === productId);
        if (!row) return;

        const bp = taskProduct.storehouse_product?.batch_products?.[0];
        const quantity = Number(taskProduct.count) || 0;
        const isPicked = !!taskProduct.is_picked;

        if (isPicked) {
          row.picked_quantity = Math.round((row.picked_quantity + quantity) * 1000) / 1000;
          if (taskProduct.is_overpick) row.has_overpick = true;
        }

        let label;
        if (bp?.serial_number) {
          label = bp.serial_number;
        } else if (row.is_single) {
          label = 'Без S/N';
        } else {
          label = (isPicked ? 'Взято ' : 'Запланировано ') + this.formatQuantity(quantity);
        }

        row.planned_serials.push({
          serial_number: bp?.serial_number || null,
          task_product_id: taskProduct.id,
          is_picked: isPicked,
          is_overpick: !!taskProduct.is_overpick,
          label,
        });
      });
    },

    // Проверить есть ли шаги с компонентами
    hasComponentSteps(operation) {
      return operation.steps?.some(step => 
        step.operation_step?.components && step.operation_step.components.length > 0
      );
    },

    // Шаги спецификации идут в порядке сортировки из спецификации
    getSortedOperations(task) {
      return (task.operations || [])
        .slice()
        .sort((a, b) => (a.operation?.sort || 0) - (b.operation?.sort || 0));
    },

    // Сводка по шагам задания: сколько закрыто из скольких
    /**
     * Данные о выпуске продукта для цифрового паспорта (Z-28): решение службы качества
     * по акту выпуска, дата, кто заполнил и кто согласовал. Возвращает null, если акта
     * нет — изделие до выпуска ещё не дошло, и блок не показывается.
     */
    getReleaseInfo(task) {
      const check = task.batch_products?.[0]?.smk_check;
      if (!check) return null;

      const labels = {
        approved: { statusLabel: 'Выпуск согласован', statusClass: 'text-emerald-700' },
        rejected: { statusLabel: 'Выпуск отклонён', statusClass: 'text-red-600' },
        pending: { statusLabel: 'Ожидает решения', statusClass: 'text-amber-600' },
      };
      const label = labels[check.status] || { statusLabel: check.status, statusClass: 'text-slate-800' };

      return {
        ...label,
        decidedAt: this.formatReleaseDateTime(check.approved_at || check.checked_at),
        checkedBy: this.releaseUserName(check.checked_by_user),
        approvedBy: this.releaseUserName(check.approved_by_user),
        comment: check.comment || '',
      };
    },

    /** ФИО сотрудника либо прочерк: у ожидающего решения акта согласующего ещё нет */
    releaseUserName(user) {
      if (!user) return '—';
      const parts = [user.last_name, user.first_name, user.middle_name].filter(Boolean);

      return parts.length ? parts.join(' ') : (user.email || '—');
    },

    formatReleaseDateTime(value) {
      if (!value) return '—';
      const date = new Date(value);

      return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    getStepsProgress(task) {
      const operations = task.operations || [];
      const total = operations.length;
      const done = operations.filter(op => op.status === 'done').length;

      return {
        done,
        total,
        percent: total > 0 ? Math.round((done / total) * 100) : 0,
      };
    },

    isOutOfOrder(operation) {
      return Boolean(this.outOfOrderOperations[operation.id]);
    },

    getOutOfOrderReason(operation) {
      const blockingName = this.outOfOrderOperations[operation.id];
      if (!blockingName) return null;

      return `Шаг начат ${this.getDateTime(operation.fact_start_datetime)}, `
        + `до завершения предыдущего по спецификации шага «${blockingName}»`;
    },

    getOutOfOrderCount(task) {
      return (task.operations || []).filter(operation => this.isOutOfOrder(operation)).length;
    },

    // Цвет кружка с номером шага в свёрнутой строке
    getOperationBulletClass(status) {
      switch (status) {
        case 'done':
          return 'bg-green-500 text-white';
        case 'work':
          return 'bg-blue-500 text-white';
        case 'new':
          return 'bg-slate-200 text-slate-600';
        default:
          return 'bg-slate-200 text-slate-600';
      }
    },

    // Раскрыть/свернуть подробности одного шага
    toggleOperation(operationId) {
      const index = this.expandedOperations.indexOf(operationId);
      if (index === -1) {
        this.expandedOperations.push(operationId);
      } else {
        this.expandedOperations.splice(index, 1);
      }
    },

    areAllOperationsExpanded(task) {
      const operations = task.operations || [];
      return operations.length > 0
        && operations.every(op => this.expandedOperations.includes(op.id));
    },

    // Раскрыть/свернуть все шаги задания разом
    toggleAllOperations(task) {
      const operationIds = (task.operations || []).map(op => op.id);

      if (this.areAllOperationsExpanded(task)) {
        this.expandedOperations = this.expandedOperations.filter(id => !operationIds.includes(id));
        return;
      }

      operationIds.forEach(id => {
        if (!this.expandedOperations.includes(id)) {
          this.expandedOperations.push(id);
        }
      });
    },

    // Переключить раскрытие всех компонентов операции
    toggleOperationProducts(operationId) {
      const index = this.expandedOperationProducts.indexOf(operationId);
      if (index === -1) {
        this.expandedOperationProducts.push(operationId);
      } else {
        this.expandedOperationProducts.splice(index, 1);
      }
    },

    // Блок «Используемое ПО» изделия в режиме правки
    isEditingSoftwareKeys(task) {
      return Object.prototype.hasOwnProperty.call(this.softwareKeyDrafts, task.id);
    },

    // «Изменить»: поля ввода с текущими ключами у всех ПО, которые можно править
    startSoftwareKeysEdit(task) {
      const drafts = {};
      task.software_keys.forEach((software) => {
        drafts[software.product_id] = software.activation_key || '';
      });
      this.softwareKeyDrafts = { ...this.softwareKeyDrafts, [task.id]: drafts };
    },

    // «Отмена»: введённое отбрасывается, на экране остаются сохранённые ключи
    cancelSoftwareKeysEdit(task) {
      const drafts = { ...this.softwareKeyDrafts };
      delete drafts[task.id];
      this.softwareKeyDrafts = drafts;
    },

    // «Сохранить»: изменённые ключи блока уходят одним запросом — сохраняются все или ни один
    async saveSoftwareKeys(task) {
      const drafts = this.softwareKeyDrafts[task.id] || {};
      const keys = task.software_keys
        .filter((software) => software.is_editable !== false)
        .filter((software) => (drafts[software.product_id] || '').trim() !== (software.activation_key || ''))
        .map((software) => ({
          product_id: software.product_id,
          activation_key: (drafts[software.product_id] || '').trim() || null,
        }));

      // Ничего не меняли — просто выходим из режима правки
      if (keys.length === 0) {
        this.cancelSoftwareKeysEdit(task);
        return;
      }

      this.savingSoftwareTaskId = task.id;
      try {
        const response = await axios.post('/api/productionOrder/saveSoftwareActivationKeys', {
          production_task_id: task.id,
          keys,
        });
        if (response.data?.success && Array.isArray(response.data?.data)) {
          task.software_keys = response.data.data;
          this.cancelSoftwareKeysEdit(task);
        } else {
          alert(response.data?.message || 'Не удалось сохранить ключи, попробуйте ещё раз');
        }
      } catch (error) {
        console.error('Ошибка сохранения ключей активации ПО:', error);
        // Правку заполненного ключа сервер отклоняет только у запущенного заказа: страница
        // об этом не знала — закрываем заполненные ключи, как после перезагрузки.
        // Введённое в остальные поля остаётся — сохранение можно повторить
        if (error.response?.status === 422 && error.response?.data?.errors?.activation_key) {
          task.software_keys.forEach((software) => {
            if (software.activation_key) {
              software.is_editable = false;
              drafts[software.product_id] = software.activation_key;
            }
          });
        }
        alert(error.response?.data?.message || 'Не удалось сохранить ключи, попробуйте ещё раз');
      } finally {
        this.savingSoftwareTaskId = null;
      }
    },

    // Получить использованные компоненты для конкретного шага операции
    // Карточки строятся из компонентов шага (все, и серийные, и нет).
    // task_products — только источник конкретики: по совпадению product_id
    // заменяют представление компонента; компоненты без совпадения добиваются
    // из самого компонента (наименование + требуемое кол-во).
    getStepTaskProducts(step, operation) {
      // ПО на складе не хранится и комплектующим не является — его показывает
      // отдельный блок «Используемое ПО», а не таблица компонентов шага
      if (step.operation_step?.type === 'software') return [];

      const components = step.operation_step?.components || [];
      if (components.length === 0) return [];

      const taskProducts = operation.task_products || [];
      const result = [];

      components.forEach(comp => {
        const compProductId = comp.product?.id;
        if (!compProductId) return;

        const matched = taskProducts.filter(tp => {
          // task_products приходят по всей задаче — позиции чужих операций отсеиваем.
          // Без этого взятое на комплектовании показывается и в блоках операций, которые
          // ещё не начинались, вместе с пометкой перерасхода (AND-213)
          if (tp.operation_id && operation.id && tp.operation_id !== operation.id) return false;

          const tpProductId = tp.storehouse_product?.batch_product?.product_id
                           || tp.product?.id;
          return tpProductId === compProductId;
        });

        if (matched.length > 0) {
          matched.forEach(tp => result.push(tp));
        } else {
          result.push({
            product: comp.product,
            count: comp.count,
            storehouse_product: null,
          });
        }
      });

      return result;
    },

    // Рабочий центр операции: пост из спецификации известен сразу после создания задания,
    // стол добавляется, когда операцию взяли в работу
    getWorkCenterLabel(operation) {
      const post = operation.operation?.workshop_post?.name;
      const unit = operation.workshop_post_unit?.name;

      if (post && unit) return `${post} — ${unit}`;

      return post || unit || '';
    },

    // Признак приходит с сервера: печать возможна, только если для операции
    // определяется склад — по назначенному столу либо по посту из спецификации
    canPrintTask(operation) {
      return Boolean(operation.can_print_picking_task);
    },

    // Печатные формы отвечают HTML, поэтому причина отказа приходит либо строкой, либо JSON
    getPrintErrorMessage(error, fallback) {
      const data = error.response?.data;
      const message = typeof data === 'string' ? data : data?.message;

      return message || fallback;
    },

    // Печать задания на комплектование
    async printPickingTask(operation) {
      try {
        const response = await axios.get('/api/productionOrder/printPickingTask', {
          params: {
            production_order_id: this.order.id,
            operation_id: operation.id,
          },
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error('Разрешите всплывающие окна');

        printWindow.document.write(response.data);
        printWindow.document.close();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error('Ошибка печати задания на комплектование:', error);
        alert(this.getPrintErrorMessage(error, 'Не удалось распечатать задание на комплектование'));
      }
    },

    // Печать формы «Перемещение»
    async printMovementTask(operation) {
      try {
        const response = await axios.get('/api/productionOrder/printMovementTask', {
          params: {
            production_order_id: this.order.id,
            operation_id: operation.id,
          },
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error('Разрешите всплывающие окна');

        printWindow.document.write(response.data);
        printWindow.document.close();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error('Ошибка печати перемещения:', error);
        alert(this.getPrintErrorMessage(error, 'Не удалось распечатать перемещение'));
      }
    },

    // Проверить является ли операция операцией комплектования
    isPickingOperation(operation) {
      return (operation.steps || []).some(s => s.operation_step?.type === 'picking');
    },

    // Открыть модалку для планирования компонентов
    openComponentModal(operation) {
      const componentStep = (operation.steps || []).find(s => s.operation_step?.type === 'picking');

      if (!componentStep) return;

      // Определяем product_id у которых лимит ещё не достигнут.
      // Планируются только штучные позиции — количественные в модалке не участвуют
      const requiredComps = this.getRequiredComponents(operation);
      const allowedProductIds = requiredComps
        .filter(c => c.is_single && c.planned_serials.length < c.required_quantity)
        .map(c => c.product_id);

      this.$refs.componentModal.openModal(componentStep.operation_step.id, operation.production_task_id, allowedProductIds);
    },

    // Обработчик сохранения в модалке
    onComponentSaved() {
      // Перезагружаем данные заказа
      this.fetchOrder(this.$route.params.id);
    },

    // Удалить запланированный компонент
    async deletePlannedComponent(operation, productId, taskProductId) {
      if (!confirm('Удалить запланированный компонент?')) return;

      try {
        await axios.delete(`/api/component-planning/delete-planned/${taskProductId}`);
        this.fetchOrder(this.$route.params.id);
      } catch (error) {
        alert('Ошибка удаления: ' + (error.response?.data?.error || error.message));
      }
    },

    // Получить шаги печати для операции напрямую из operation_steps
    getPrintStepsFromOperation(operation) {
      if (!operation.operation?.steps) return [];
      
      return operation.operation.steps.filter(step => 
        step.type === 'print' && 
        step.documents && 
        step.documents.length > 0
      ).sort((a, b) => (a.sort || 0) - (b.sort || 0));
    },

    // Получить все шаги печати для задачи
    getAllPrintStepsFromTask(task) {
      const printSteps = [];
      task.operations.forEach(operation => {
        printSteps.push(...this.getPrintStepsFromOperation(operation));
      });
      return printSteps;
    },

    // Получить названия документов для шага
    getStepDocumentNames(step) {
      if (!step.documents || !step.documents.length) return 'Нет документов';
      return step.documents.map(doc => doc.name).join(', ');
    },

    // Извлечь прикреплённые для печати файлы (attachment_type = 'document') из вложений
    extractAttachedFiles(attachments) {
      if (!Array.isArray(attachments)) return [];
      return attachments
        .filter(a => a.attachment_type === 'document' && (a.file || a.file_id))
        .map(a => ({
          id: a.file?.id || a.file_id,
          name: a.file?.original_name || a.file?.name || 'Документ',
        }));
    },

    // Сгруппированные прикреплённые файлы по операциям и шагам задачи
    getAttachedPrintFilesFromTask(task) {
      const groups = [];
      const operations = (task.operations || [])
        .slice()
        .sort((a, b) => (a.operation?.sort || 0) - (b.operation?.sort || 0));

      operations.forEach(operation => {
        const op = operation.operation;
        if (!op) return;
        const opName = op.name || 'Операция без названия';

        // Файлы, прикреплённые к операции в спецификации
        const opFiles = this.extractAttachedFiles(op.file_attachments);
        // Файлы, приложенные на рабочем столе при выполнении операции
        const runtimeFiles = this.extractAttachedFiles(operation.file_attachments);
        const allOpFiles = [...opFiles, ...runtimeFiles];
        if (allOpFiles.length > 0) {
          groups.push({ key: 'op-' + (operation.id || op.id), title: opName, files: allOpFiles });
        }

        // Файлы, прикреплённые к шагам операции
        (op.steps || [])
          .slice()
          .sort((a, b) => (a.sort || 0) - (b.sort || 0))
          .forEach((step, stepIndex) => {
            const stepFiles = this.extractAttachedFiles(step.file_attachments);
            if (stepFiles.length > 0) {
              groups.push({
                key: 'step-' + step.id,
                title: `${opName} — ${stepIndex + 1}. ${step.name || 'Шаг'}`,
                files: stepFiles,
              });
            }
          });
      });

      return groups;
    },

    // Печать прикреплённого файла (PDF) через предпросмотр
    printAttachedFile(fileId) {
      if (!fileId) return;
      const printWindow = window.open(`/api/files/${fileId}/preview`, '_blank');
      if (!printWindow) {
        alert('Разрешите всплывающие окна');
        return;
      }
      printWindow.addEventListener('load', () => {
        setTimeout(() => {
          try {
            printWindow.print();
          } catch (e) {
            console.error('Ошибка печати файла:', e);
            alert('Не удалось отправить файл на печать. Документ открыт в новой вкладке — распечатайте его вручную.');
          }
        }, 500);
      });
    },

    // Получить операции с упаковочным шагом
    getPackagingOperations(task) {
      if (!task.operations) return [];
      return task.operations.filter(op =>
        op.operation?.steps?.some(step => step.type === 'packaging')
      );
    },

    // Получить завершённые операции с упаковочным шагом
    getDonePackagingOperations(task) {
      return this.getPackagingOperations(task).filter(op => op.status === 'done');
    },

    // Печать упаковочного документа
    async printPackingDocument(type, task) {
      const urls = {
        order: '/api/packingPlace/printPackingListOrder',
        place: '/api/packingPlace/printPackingListPlace',
        labels: '/api/packingPlace/printTransportLabels',
      };

      try {
        const response = await axios.get(urls[type], {
          params: { production_task_id: task.id },
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Разрешите всплывающие окна');
          return;
        }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error('Ошибка печати:', error);
        alert('Ошибка печати: ' + (error.response?.data?.message || error.message));
      }
    },

    // Новый метод печати через operation_step_id
    async printOperationStep(operationStep, task) {
      if (!operationStep.documents || !operationStep.documents.length) {
        alert('Для данного шага не найдены документы для печати');
        return;
      }

      const batchProduct = task.batch_products?.[0];
      const productId = batchProduct?.product?.id
        || this.order?.order_product?.product?.id
        || this.order?.product?.id;

      if (!productId) {
        alert('Не найдены данные продукции для печати');
        return;
      }

      const operation = task.operations?.find(op => op.operation?.steps?.some(s => s.id === operationStep.id));

      const taskData = {
        product_id: productId,
        batch_product_id: batchProduct?.id || null,
        production_task_operation_id: operation?.id ?? null,
        operation_step_id: operationStep.id ?? null,
      };

      for (const document of operationStep.documents) {
        try {
          await this.printDocumentFromStep(operationStep, document, taskData);
        } catch (error) {
          console.error(`Ошибка печати документа ${document.name}:`, error);
        }
      }
    },

    // Печать одного документа (document_id + batch_product_id, компоненты из продукции)
    async printDocumentFromStep(operationStep, document, taskData) {
      if (!document?.id) {
        alert('Документ не содержит id');
        return;
      }
      if (!taskData?.batch_product_id) {
        alert('Не найден batch_product_id для печати');
        return;
      }

      try {
        const response = await axios.post('/api/documents/render', {
          document_id: document.id,
          batch_product_id: taskData.batch_product_id,
          operation_step_id: taskData.operation_step_id ?? operationStep?.id,
          production_task_operation_id: taskData.production_task_operation_id,
          // template: document.template_key,
          // document,
          // task_data: taskData,
          // is_multiple: false,
          // document_index: 0,
          // document_count: 1,
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error('Разрешите всплывающие окна');

        printWindow.document.write(response.data.html);
        printWindow.document.close();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => printWindow.close(), 1000);
          }, 500);
        };
      } catch (error) {
        console.error(`Ошибка печати ${document.name}:`, error);
        alert(`Ошибка: ${error.message}`);
      }
    },

    //Не выззывается тут нигде
    // async printDocumentViaRender(document, taskData, components = []) {
    //   if (!document || !document.template_key) {
    //     alert('Документ не содержит template_key');
    //     return;
    //   }

    //   try {
    //     const response = await axios.post('/api/documents/render', {
    //       template: document.template_key,
    //       document,
    //       components,
    //       task_data: taskData,
    //       is_multiple: false,
    //       document_index: 0,
    //       document_count: 1,
    //     });

    //     const printWindow = window.open('', '_blank');
    //     if (!printWindow) throw new Error('Разрешите всплывающие окна');

    //     printWindow.document.write(response.data.html);
    //     printWindow.document.close();

    //     printWindow.onload = () => {
    //       setTimeout(() => {
    //         printWindow.print();
    //         setTimeout(() => printWindow.close(), 1000);
    //       }, 500);
    //     };
    //   } catch (error) {
    //     console.error(`Ошибка печати ${document.name}:`, error);
    //     alert(`Ошибка: ${error.message}`);
    //   }
    // },

    async fetchOperationPhotos(operation) {
      try {
        const response = await axios.post(`/api/productionOperation/photos`, { operationId: operation.id });
        if (response.data.success) {
          operation.photos = response.data.photos.map(photo => ({
            path: `/${photo.path}`,
            created_at: photo.created_at,
          }));
        } else {
          console.error('Ошибка при получении фотографий:', response.data.message);
          operation.photos = [];
        }
      } catch (error) {
        console.error('Ошибка при запросе фотографий:', error);
        operation.photos = [];
      }
    },

    /**
     * Развернуть задание и прокрутить к нему (по task_id)
     */
    scrollToTask(taskId) {
      const tid = Number(taskId);
      if (!tid || !this.order || !this.order.tasks) return;

      const idx = this.order.tasks.findIndex((t) => Number(t.id) === tid);
      if (idx === -1) return;

      if (!this.expandedTasks.includes(idx)) {
        this.expandedTasks.push(idx);
      }

      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById('task-' + tid);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-green-400', 'ring-offset-2');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-green-400', 'ring-offset-2');
          }, 2500);
          return;
        }
        if (attempts++ < 20) {
          setTimeout(tryScroll, 100);
        }
      };
      this.$nextTick(() => setTimeout(tryScroll, 100));
    },

    /**
     * Развернуть задание с указанной операцией и прокрутить к ней
     */
    scrollToOperation(operationId) {
      const opId = Number(operationId);
      if (!opId || !this.order || !this.order.tasks) return;

      let taskIndex = -1;
      this.order.tasks.forEach((task, idx) => {
        if ((task.operations || []).some((op) => Number(op.id) === opId)) {
          taskIndex = idx;
        }
      });
      if (taskIndex === -1) return;

      // Разворачиваем задание с этой операцией
      if (!this.expandedTasks.includes(taskIndex)) {
        this.expandedTasks.push(taskIndex);
      }

      // И сам шаг спецификации, иначе будут видны только свёрнутые строки
      if (!this.expandedOperations.includes(opId)) {
        this.expandedOperations.push(opId);
      }

      // Ждём, пока операция отрендерится в DOM (до ~2 секунд), затем скроллим
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById('operation-' + opId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Кратковременная подсветка операции
          el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-2xl');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-2xl');
          }, 2500);
          return;
        }
        if (attempts++ < 20) {
          setTimeout(tryScroll, 100);
        }
      };
      this.$nextTick(() => setTimeout(tryScroll, 100));
    },

    toggleTask(taskIndex) {
      const index = this.expandedTasks.indexOf(taskIndex);
      if (index > -1) {
        this.expandedTasks.splice(index, 1);
      } else {
        this.expandedTasks.push(taskIndex);
      }
    },

    startSerialEdit(task) {
      const bp = task.batch_products?.[0];
      if (!bp?.id) {
        return;
      }
      this.editingSerialTaskId = task.id;
      this.serialEditDraft = bp.serial_number != null ? String(bp.serial_number) : '';
    },

    cancelSerialEdit() {
      this.editingSerialTaskId = null;
      this.serialEditDraft = '';
    },

    async saveSerialNumber(task) {
      const bp = task.batch_products?.[0];
      if (!bp?.id || this.editingSerialTaskId !== task.id) {
        return;
      }
      const trimmed = this.serialEditDraft.trim();
      const serialForApi = trimmed || null;

      this.serialSaving = true;
      try {
        const response = await axios.post('/api/batchProduct/updateSerialNumber', {
          id: bp.id,
          serial_number: serialForApi,
        });
        if (response.data?.success && response.data?.data) {
          bp.serial_number = response.data.data.serial_number;
          this.cancelSerialEdit();
        } else {
          alert(response.data?.message || 'Не удалось сохранить серийный номер');
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Ошибка сохранения';
        alert(msg);
      } finally {
        this.serialSaving = false;
      }
    },

    getDateTime(date) {
      if (!date) return 'N/A';
      return moment(date).local().format('DD.MM.YYYY HH:mm');
    },

    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    getUnitLabel(product) {
      return product?.unit_short_name || product?.unit?.short_name || 'шт';
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const momentDate = moment(date);
      if (!momentDate.isValid()) return 'N/A';
      return momentDate.local().format('DD.MM.YYYY');
    },

    formatPhotoDateTime(date) {
      if (!date) return 'Дата неизвестна';
      return moment(date).local().format('DD.MM.YYYY в HH:mm:ss');
    },

    getDuration(start, end) {
      if (!start || !end) return 'N/A';
      const duration = moment.duration(moment(end).diff(moment(start)));
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();
      if (hours > 0) {
        return `${hours}ч ${minutes}мин ${seconds}сек`;
      }
      if (minutes > 0) {
        return `${minutes}мин ${seconds}сек`;
      }
      return `${seconds}сек`;
    },

    // Подпись состояния изделия: сырой receipt_status на экран не выводим
    getProductStatusBadge(task) {
      const status = task?.batch_products?.[0]?.receipt_status;
      const badges = {
        defect: { label: 'Брак', class: 'bg-red-100 text-red-700' },
        rework_in_production: { label: 'Доработка', class: 'bg-orange-100 text-orange-700' },
        nc_stopped: { label: 'Остановлено: несоответствие', class: 'bg-red-100 text-red-700' },
      };
      return badges[status] || null;
    },

    // Признаки незакрытого случая несоответствия по изделию (ТЗ-7.10): пометка
    // «расследование не ведётся» и подсветка залежавшегося. Считает их бэкенд —
    // тот же источник, что и у реестра несоответствий, второй логики на фронте нет
    getNonconformityFlags(task) {
      return task?.batch_products?.[0]?.nonconformity || null;
    },

    // Документы качества изделия (бэкенд отдаёт их уже с подписями, свежие сверху)
    getQualityDocuments(task) {
      const documents = [];
      (task?.batch_products || []).forEach(batchProduct => {
        (batchProduct.quality_documents || []).forEach(doc => {
          documents.push({ ...doc, key: `${doc.type}-${doc.id}` });
        });
      });
      return documents;
    },

    // Активное расследование несоответствия по изделию: самое свежее из незакрытых.
    // Документы приходят с бэкенда уже отсортированными (свежие сверху) и с подписями,
    // отменённое расследование активным не считается
    getActiveInvestigation(task) {
      return this.getQualityDocuments(task).find(
        doc => doc.type === 'nc_investigation' && doc.status !== 'cancelled'
      ) || null;
    },

    // Ссылка на документ может быть внешней — тогда обычный href, а не маршрут SPA
    isExternalLink(link) {
      return typeof link === 'string' && /^https?:\/\//i.test(link);
    },

    // Открыть подтверждение возврата остановленного изделия в работу
    openResumeModal(task) {
      this.resumeModal = {
        show: true,
        taskId: task.id,
        reason: task.stopped_reason || '',
        resumeReason: '',
        error: '',
      };
    },

    closeResumeModal() {
      if (this.resumingTaskId !== null) return;
      this.resumeModal = { show: false, taskId: null, reason: '', resumeReason: '', error: '' };
    },

    // Снятие остановки без документов — на случай ошибочного нажатия «Карантин производства»
    async resumeProduction() {
      const taskId = this.resumeModal.taskId;
      if (!taskId || this.resumingTaskId !== null) return;

      const resumeReason = (this.resumeModal.resumeReason || '').trim();
      if (resumeReason === '') {
        this.resumeModal = { ...this.resumeModal, error: 'Укажите причину возврата в работу.' };
        return;
      }

      this.resumingTaskId = taskId;
      this.resumeModal = { ...this.resumeModal, error: '' };
      try {
        const { data } = await axios.post('/api/nonconformity/resumeProduction', {
          production_task_id: taskId,
          reason: resumeReason,
        });
        if (data?.success) {
          this.resumeModal = { show: false, taskId: null, reason: '', resumeReason: '', error: '' };
          this.resumingTaskId = null;
          await this.fetchOrder(this.$route.params.id);
          return;
        }
        this.resumeModal = { ...this.resumeModal, error: data?.message || 'Не удалось вернуть изделие в работу' };
      } catch (error) {
        const payload = error?.response?.data;
        const details = payload?.errors && typeof payload.errors === 'object'
          ? Object.values(payload.errors).flat().filter(Boolean)
          : [];
        this.resumeModal = {
          ...this.resumeModal,
          error: details.length ? details.join('\n') : (payload?.message || 'Не удалось вернуть изделие в работу'),
        };
      } finally {
        this.resumingTaskId = null;
      }
    },

    /**
     * Условия действия по одному изделию.
     *
     * Сбой не проглатываем: возвращаем признак ошибки, чтобы карточка показала отличимое
     * состояние «не удалось загрузить» с возможностью повторить. Молчаливое отсутствие
     * кнопки читалось бы как «действие запрещено», а это разные вещи.
     */
    async requestActionOptions(url, task, logMessage) {
      try {
        const { data } = await axios.post(url, {
          batch_product_id: task.batch_products[0].id,
        });
        if (data?.success) {
          return { taskId: task.id, options: data.data, failed: false };
        }
        console.error(logMessage, data);
        return { taskId: task.id, options: null, failed: true };
      } catch (error) {
        console.error(logMessage, error);
        return { taskId: task.id, options: null, failed: true };
      }
    },

    // Один тост на загрузку, а не на каждое изделие: у заказа с несколькими
    // остановленными изделиями сбой обычно общий
    notifyOptionsError(message) {
      if (this.$root?.$toast?.error) this.$root.$toast.error(message);
    },

    isOptionsRetrying(kind, taskId) {
      return Boolean(this.optionsRetrying[`${kind}-${taskId}`]);
    },

    setOptionsRetrying(kind, taskId, value) {
      const key = `${kind}-${taskId}`;
      if (value) {
        this.optionsRetrying = { ...this.optionsRetrying, [key]: true };
        return;
      }
      const next = { ...this.optionsRetrying };
      delete next[key];
      this.optionsRetrying = next;
    },

    /**
     * Условия штатного возврата на маршрут — только по изделиям, у которых возврат
     * в принципе возможен (см. actionOptionCandidates).
     */
    async loadRouteReturnOptions() {
      this.routeReturnOptions = {};
      this.routeReturnErrors = {};

      const candidates = this.actionOptionCandidates;
      if (candidates.length === 0) return;

      const results = await Promise.all(candidates.map(task => this.requestActionOptions(
        '/api/nonconformity/getReturnOptions',
        task,
        'Не удалось получить условия возврата на маршрут:',
      )));

      const options = {};
      const errors = {};
      results.forEach(({ taskId, options: data, failed }) => {
        if (failed) {
          errors[taskId] = true;
          return;
        }
        options[taskId] = data;
      });
      this.routeReturnOptions = options;
      this.routeReturnErrors = errors;

      if (Object.keys(errors).length > 0) this.notifyOptionsError(ROUTE_RETURN_LOAD_ERROR);
    },

    // Повторная загрузка условий возврата по одному изделию — из состояния «не удалось загрузить»
    async retryRouteReturnOptions(task) {
      if (!task?.batch_products?.[0]?.id || this.isOptionsRetrying('routeReturn', task.id)) return;

      this.setOptionsRetrying('routeReturn', task.id, true);
      try {
        const { options, failed } = await this.requestActionOptions(
          '/api/nonconformity/getReturnOptions',
          task,
          'Не удалось получить условия возврата на маршрут:',
        );
        if (failed) {
          this.notifyOptionsError(ROUTE_RETURN_LOAD_ERROR);
          return;
        }
        this.routeReturnOptions = { ...this.routeReturnOptions, [task.id]: options };
        const errors = { ...this.routeReturnErrors };
        delete errors[task.id];
        this.routeReturnErrors = errors;
      } finally {
        this.setOptionsRetrying('routeReturn', task.id, false);
      }
    },

    openRouteReturnModal(task) {
      const options = this.routeReturnOptions[task?.id] || null;
      if (!options || !options.is_available) return;

      this.routeReturnModal = {
        show: true,
        taskId: task.id,
        batchProductId: options.batch_product_id || task.batch_products?.[0]?.id || null,
        nonconformityCaseId: options.nonconformity_case_id || null,
        options,
      };
    },

    closeRouteReturnModal() {
      this.routeReturnModal = { show: false, taskId: null, batchProductId: null, nonconformityCaseId: null, options: null };
    },

    // Изделие вернулось на маршрут — перечитываем заказ, чтобы обновить операции и отметки
    async onReturnedToRoute() {
      await this.fetchOrder(this.$route.params.id);
    },

    /**
     * Условия замены — по тем же изделиям, что и условия возврата: замена возможна только
     * у остановленного изделия, а уже заменённое (окончательный брак) из кандидатов
     * исключено — иначе каждая новая замена добавляла бы по запросу на каждое открытие
     * карточки, а ответ по ней всегда один и тот же отказ.
     */
    async loadReplacementOptions() {
      this.replacementOptions = {};
      this.replacementErrors = {};

      const candidates = this.actionOptionCandidates;
      if (candidates.length === 0) return;

      const results = await Promise.all(candidates.map(task => this.requestActionOptions(
        '/api/nonconformity/getReplacementOptions',
        task,
        'Не удалось получить условия замены изделия:',
      )));

      const options = {};
      const errors = {};
      results.forEach(({ taskId, options: data, failed }) => {
        if (failed) {
          errors[taskId] = true;
          return;
        }
        options[taskId] = data;
      });
      this.replacementOptions = options;
      this.replacementErrors = errors;

      if (Object.keys(errors).length > 0) this.notifyOptionsError(REPLACEMENT_LOAD_ERROR);
    },

    // Повторная загрузка условий замены по одному изделию
    async retryReplacementOptions(task) {
      if (!task?.batch_products?.[0]?.id || this.isOptionsRetrying('replacement', task.id)) return;

      this.setOptionsRetrying('replacement', task.id, true);
      try {
        const { options, failed } = await this.requestActionOptions(
          '/api/nonconformity/getReplacementOptions',
          task,
          'Не удалось получить условия замены изделия:',
        );
        if (failed) {
          this.notifyOptionsError(REPLACEMENT_LOAD_ERROR);
          return;
        }
        this.replacementOptions = { ...this.replacementOptions, [task.id]: options };
        const errors = { ...this.replacementErrors };
        delete errors[task.id];
        this.replacementErrors = errors;
      } finally {
        this.setOptionsRetrying('replacement', task.id, false);
      }
    },

    openReplacementModal(task) {
      const options = this.replacementOptions[task?.id] || null;
      if (!options || !options.is_available) return;

      this.replacementModal = {
        show: true,
        taskId: task.id,
        options,
        selectedOperationId: options.from_operation_id ?? null,
        submitting: false,
        error: '',
        result: null,
      };
    },

    closeReplacementModal() {
      if (this.replacementModal.submitting) return;
      this.replacementModal = {
        show: false,
        taskId: null,
        options: null,
        selectedOperationId: null,
        submitting: false,
        error: '',
        result: null,
      };
    },

    // Заведение замены. Изделие адресуется случаем или самим изделием — бэкенду
    // достаточно любого из двух; операция передаётся только если она известна
    async submitReplacement() {
      if (this.replacementModal.submitting) return;

      const options = this.replacementModal.options;
      const payload = {};
      if (options?.nonconformity_case_id) payload.nonconformity_case_id = options.nonconformity_case_id;
      if (options?.batch_product_id) payload.batch_product_id = options.batch_product_id;

      if (!payload.nonconformity_case_id && !payload.batch_product_id) {
        this.replacementModal = { ...this.replacementModal, error: 'Не указано, какое изделие заменять.' };
        return;
      }

      if (this.replacementModal.selectedOperationId) {
        payload.production_task_operation_id = this.replacementModal.selectedOperationId;
      }

      this.replacementModal = { ...this.replacementModal, submitting: true, error: '' };
      try {
        const { data } = await axios.post('/api/nonconformity/replaceProduct', payload);
        if (data?.success) {
          const result = data.data || {};
          this.replacementModal = { ...this.replacementModal, submitting: false, error: '', result };
          // Заказ перечитываем сразу: в паспорте появляется задание-замена, забракованный
          // экземпляр остаётся на месте со своими документами, а связь между ними
          // приходит в самом заказе — она же остаётся после перезагрузки страницы
          await this.fetchOrder(this.$route.params.id);
          return;
        }
        this.replacementModal = {
          ...this.replacementModal,
          submitting: false,
          error: data?.message || 'Не удалось завести замену изделия.',
        };
      } catch (error) {
        this.replacementModal = {
          ...this.replacementModal,
          submitting: false,
          error: this.extractApiError(error, 'Не удалось завести замену изделия.'),
        };
      }
    },

    // Операция комплектования опознаётся по шагу типа picking в данных заказа:
    // в ответе об условиях замены признака нет, а правило переноса операций его требует
    isPickingOperationById(operationId) {
      const operation = this.findOperationById(operationId);
      return operation ? this.isPickingOperation(operation) : false;
    },

    operationTitle(operation) {
      return operation?.name || 'Операция без названия';
    },

    // Подпись в закрытом селекте: сырые ключи статуса на экран не выводим
    replacementOperationLabel(operation) {
      const parts = [this.operationTitle(operation)];
      if (operation?.workshop_post_name) parts.push(operation.workshop_post_name);
      if (operation?.status_label) parts.push(operation.status_label);
      return parts.join(' · ');
    },

    // Человекочитаемое обозначение изделия: внутренние id на экран не выводим
    serialLabel(serialNumber) {
      return serialNumber ? `№ ${serialNumber}` : 'без серийного номера';
    },

    // Ошибки валидации (422) приходят готовым текстом — показываем их пользователю
    extractApiError(error, fallback) {
      const payload = error?.response?.data;
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      return details.length ? details.join('\n') : (payload?.message || fallback);
    },

    getStatusLabel(status) {
      switch (status) {
        case 'new':
          return 'Очередь';
        case 'work':
          return 'В работе';
        case 'done':
          return 'Выполнено';
        default:
          return 'Неизвестно';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'new':
          return 'bg-yellow-100 text-yellow-800';
        case 'work':
          return 'bg-blue-100 text-blue-800';
        case 'done':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getTaskStatusClass(status) {
      switch (status) {
        case 'new':
          return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
        case 'work':
          return 'bg-blue-100 text-blue-800 border border-blue-200';
        case 'done':
          return 'bg-green-100 text-green-800 border border-green-200';
        default:
          return 'bg-gray-100 text-gray-800 border border-gray-200';
      }
    },

    getOperationStatusClass(status) {
      switch (status) {
        case 'new':
          return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
        case 'work':
          return 'bg-blue-50 text-blue-700 border border-blue-200';
        case 'done':
          return 'bg-green-50 text-green-700 border border-green-200';
        default:
          return 'bg-gray-50 text-gray-700 border border-gray-200';
      }
    },

    async openPhotosModal(operationId, photoIndex = 0) {
      this.currentOperationId = operationId;
      this.currentPhotoIndex = photoIndex;
      this.isPhotosModalVisible = true;
      this.photosLoading = true;

      const operation = this.findOperationById(operationId);

      if (operation && operation.photos && operation.photos.length > 0) {
        this.operationPhotos = operation.photos;
      } else {
        try {
          const response = await axios.post(`/api/productionOperation/photos`, { operationId: operationId });
          if (response.data.success) {
            this.operationPhotos = response.data.photos.map(photo => ({
              path: `/${photo.path}`,
              created_at: photo.created_at,
            }));
          } else {
            console.error('Ошибка при получении фотографий:', response.data.message);
            this.operationPhotos = [];
          }
        } catch (error) {
          console.error('Ошибка при запросе фотографий:', error);
          this.operationPhotos = [];
        }
      }
      this.photosLoading = false;
    },

    closePhotosModal() {
      this.isPhotosModalVisible = false;
      this.operationPhotos = [];
      this.currentPhotoIndex = 0;
    },

    nextPhoto() {
      if (this.currentPhotoIndex < this.operationPhotos.length - 1) {
        this.currentPhotoIndex++;
      } else {
        this.currentPhotoIndex = 0;
      }
    },

    previousPhoto() {
      if (this.currentPhotoIndex > 0) {
        this.currentPhotoIndex--;
      } else {
        this.currentPhotoIndex = this.operationPhotos.length - 1;
      }
    },

    findOperationById(operationId) {
      if (!this.order || !this.order.tasks) return null;

      for (const task of this.order.tasks) {
        for (const operation of task.operations) {
          if (operation.id === operationId) {
            return operation;
          }
        }
      }
      return null;
    }
  },

  mounted() {
    const orderId = this.$route.params.id;
    this.fetchOrder(orderId).then(() => {
      // Переход из уведомления: до задания (завершено) — приоритетнее, иначе до операции
      const taskId = this.$route.query.taskId;
      const opId = this.$route.query.operationId;
      if (taskId) {
        this.scrollToTask(parseInt(taskId));
      } else if (opId) {
        this.scrollToOperation(parseInt(opId));
      }
    });

    // Авто-погашение уведомлений по этому заказу при заходе на страницу
    const { markEntityRead } = useNotificationAutoRead();
    markEntityRead('production_order', orderId);

    // Добавляем обработчики клавиш для навигации по фото
    document.addEventListener('keydown', (e) => {
      if (this.isPhotosModalVisible && this.operationPhotos.length > 1) {
        if (e.key === 'ArrowLeft') {
          this.previousPhoto();
        } else if (e.key === 'ArrowRight') {
          this.nextPhoto();
        } else if (e.key === 'Escape') {
          this.closePhotosModal();
        }
      }
    });
  }
};
</script>

<style scoped>
/* Дополнительные стили для плавных анимаций */
.transition-all {
  transition: all 0.3s ease;
}

/* Стили для скроллбара в превью фото */
.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Анимация появления модального окна */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.fixed.inset-0 > div {
  animation: fadeIn 0.2s ease-out;
}
</style>
